/**
 * Notification Service for browser push notifications.
 *
 * Every reference to the `Notification` DOM API is guarded with a
 * `typeof` check because Safari on iOS (private mode, WKWebView, older
 * OS versions) exposes an incomplete or missing `Notification` global.
 * A single unguarded reference in the module constructor would throw a
 * ReferenceError at module load, crashing the whole React bundle boot
 * and leaving a blank black page — which is what happened before this
 * fix on Safari mobile and Chrome mobile on iOS (Chrome iOS uses
 * WKWebView under the hood).
 */

const hasNotificationAPI = () =>
  typeof window !== 'undefined' && typeof window.Notification !== 'undefined';

const getNotificationPermission = () =>
  hasNotificationAPI() ? window.Notification.permission : 'default';

class NotificationService {
  constructor() {
    this.permission = getNotificationPermission();
    this.swRegistration = null;
    this.priceThreshold = 5; // 5% change triggers notification
    this.lastPrices = new Map(); // Track last known prices
    this.watchlistMarkets = new Set(); // Markets user is watching
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return hasNotificationAPI() && 'serviceWorker' in navigator;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Notifications not supported');
      return false;
    }

    try {
      const permission = await window.Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        await this.registerServiceWorker();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Register service worker
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      this.swRegistration = registration;
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Get current permission status
   */
  getPermission() {
    if (!this.isSupported()) return 'unsupported';
    return getNotificationPermission();
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return this.getPermission() === 'granted';
  }

  /**
   * Set price change threshold (percentage)
   */
  setThreshold(percentage) {
    this.priceThreshold = percentage;
  }

  /**
   * Update watchlist markets
   */
  setWatchlist(marketIds) {
    this.watchlistMarkets = new Set(marketIds);
  }

  /**
   * Add market to watchlist
   */
  addToWatchlist(marketId) {
    this.watchlistMarkets.add(marketId);
  }

  /**
   * Remove market from watchlist
   */
  removeFromWatchlist(marketId) {
    this.watchlistMarkets.delete(marketId);
    this.lastPrices.delete(marketId);
  }

  /**
   * Check price update and trigger notification if significant
   */
  checkPriceUpdate(marketId, marketTitle, newYesPrice, newNoPrice) {
    // Only check watchlist markets
    if (!this.watchlistMarkets.has(marketId)) return;
    
    // Only notify if enabled
    if (!this.isEnabled()) return;

    const lastPrice = this.lastPrices.get(marketId);
    
    if (lastPrice) {
      const yesPriceChange = Math.abs(newYesPrice - lastPrice.yes) / lastPrice.yes * 100;
      const noPriceChange = Math.abs(newNoPrice - lastPrice.no) / lastPrice.no * 100;
      
      // Check if change exceeds threshold
      if (yesPriceChange >= this.priceThreshold || noPriceChange >= this.priceThreshold) {
        const direction = newYesPrice > lastPrice.yes ? '📈' : '📉';
        const changeText = newYesPrice > lastPrice.yes 
          ? `YES up to ${newYesPrice}¢` 
          : `YES down to ${newYesPrice}¢`;
        
        this.showNotification(
          `${direction} Price Alert`,
          `${marketTitle.substring(0, 50)}...\n${changeText}`,
          marketId,
          yesPriceChange
        );
      }
    }
    
    // Update last known prices
    this.lastPrices.set(marketId, { yes: newYesPrice, no: newNoPrice });
  }

  /**
   * Show a notification
   */
  async showNotification(title, body, marketId = null, priceChange = 0) {
    if (!this.isEnabled()) {
      console.warn('Notifications not enabled');
      return;
    }

    try {
      // Try using service worker if available
      if (this.swRegistration) {
        this.swRegistration.active?.postMessage({
          type: 'SHOW_NOTIFICATION',
          title,
          body,
          marketId,
          priceChange
        });
      } else {
        // Fallback to regular notification
        if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
          console.warn('Notification API unavailable in this browser');
          return;
        }
        new window.Notification(title, {
          body,
          icon: '/logo192.png',
          tag: `price-alert-${marketId || 'general'}`,
          requireInteraction: false
        });
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Show a test notification
   */
  async showTestNotification() {
    if (!this.isEnabled()) {
      const granted = await this.requestPermission();
      if (!granted) {
        return { success: false, message: 'Permission denied' };
      }
    }

    await this.showNotification(
      '🔔 Test Notification',
      'Price alerts are working! You\'ll be notified when watchlist markets move significantly.',
      null,
      0
    );

    return { success: true, message: 'Test notification sent' };
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
